import React, { useState, useCallback } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { VacationApplicationType } from '../../api-client';
import { getVacationApplicationTypeLabel } from '../utils/statusConverters';
import 'bootstrap-daterangepicker/daterangepicker.css';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import moment, { Moment } from 'moment';

const DATE_RANGE_LOCALE = {
  format: 'DD.MM.YYYY',
  separator: ' - ',
  applyLabel: 'Применить',
  cancelLabel: 'Отмена',
  fromLabel: 'от',
  toLabel: 'до',
  customRangeLabel: 'Выбрать диапазон',
  weekLabel: 'Н',
  daysOfWeek: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
  monthNames: [
    'Январь',
    'Февраль',
    'Март',
    'Апрель',
    'Май',
    'Июнь',
    'Июль',
    'Август',
    'Сентябрь',
    'Октябрь',
    'Ноябрь',
    'Декабрь',
  ],
  firstDay: 1,
};

const DATE_RANGE_SETTINGS = {
  locale: DATE_RANGE_LOCALE,
  ranges: {
    Сегодня: [moment(), moment()],
    'Последние 7 дней': [moment().subtract(6, 'days'), moment()],
    'Последние 30 дней': [moment().subtract(29, 'days'), moment()],
    'Этот месяц': [moment().startOf('month'), moment().endOf('month')],
    'Следующий месяц': [
      moment().add(1, 'month').startOf('month'),
      moment().add(1, 'month').endOf('month'),
    ],
  },
};

const MemoizedDateRangePicker = React.memo(
  ({
    label,
    onDatesChange,
  }: {
    label: string;
    onDatesChange: (start: string, end: string, label: string) => void;
  }) => {
    const handleCallback = useCallback(
      (start: Moment, end: Moment) => {
        onDatesChange(
          start.format('YYYY-MM-DD'),
          end.format('YYYY-MM-DD'),
          `${start.format('DD.MM.YYYY')} - ${end.format('DD.MM.YYYY')}`
        );
      },
      [onDatesChange]
    );

    return (
      <DateRangePicker initialSettings={DATE_RANGE_SETTINGS} onCallback={handleCallback}>
        <Form.Control type="text" value={label} readOnly style={{ cursor: 'pointer' }} />
      </DateRangePicker>
    );
  }
);

interface VacationCreateFormProps {
  onSubmit: (
    type: VacationApplicationType,
    fileUrl: string,
    startDate: string,
    endDate: string,
    fileName?: string
  ) => Promise<void>;
  onUploadFile: (file: File) => Promise<{ fileUrl: string }>;
  onGetTemplate: (type: VacationApplicationType) => Promise<string>;
  onCancel?: () => void;
  submitLabel?: string;
}

const VacationCreateForm: React.FC<VacationCreateFormProps> = ({
  onSubmit,
  onUploadFile,
  onGetTemplate,
  onCancel,
  submitLabel = 'Создать заявку',
}) => {
  const [type, setType] = useState<VacationApplicationType>(VacationApplicationType.PAID);
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [startDate, setStartDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [dateRangeLabel, setDateRangeLabel] = useState<string>(
    `${moment().format('DD.MM.YYYY')} - ${moment().format('DD.MM.YYYY')}`
  );

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      setError(null);
      const fileUrl = await onGetTemplate(type);
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err.message || 'Ошибка при загрузке шаблона');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleDatesChange = useCallback((start: string, end: string, label: string) => {
    setStartDate(start);
    setEndDate(end);
    setDateRangeLabel(label);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Пожалуйста, загрузите файл заявления');
      return;
    }

    if (!startDate || !endDate) {
      setError('Пожалуйста, укажите даты отпуска');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      setUploading(true);
      const { fileUrl } = await onUploadFile(file);
      setUploading(false);

      await onSubmit(type, fileUrl, startDate, endDate, file.name);
    } catch (err: any) {
      setError(err.message || 'Ошибка при создании заявки');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert variant="danger">{error}</Alert>}

      <Form.Group className="mb-3">
        <Form.Label>Тип отпуска</Form.Label>
        <div>
          {Object.values(VacationApplicationType).map((t) => (
            <Form.Check
              key={t}
              inline
              type="radio"
              label={getVacationApplicationTypeLabel(t)}
              name="vacationType"
              id={`type-${t}`}
              checked={type === t}
              onChange={() => {
                setType(t);
                setFile(null);
              }}
            />
          ))}
        </div>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Даты отпуска *</Form.Label>
        <MemoizedDateRangePicker label={dateRangeLabel} onDatesChange={handleDatesChange} />
      </Form.Group>

      <Form.Group className="mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
          >
            {downloadingTemplate ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Загрузка...
              </>
            ) : (
              'Скачать шаблон заявления'
            )}
          </Button>
        </div>
        <Form.Text className="text-muted">
          Скачайте шаблон, заполните и подпишите, затем загрузите ниже
        </Form.Text>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Заявление (подписанное)</Form.Label>
        <Form.Control
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={(e) => {
            const target = e.target as HTMLInputElement;
            setFile(target.files?.[0] || null);
          }}
          disabled={submitting}
        />
        {file && <Form.Text className="text-muted">Выбран: {file.name}</Form.Text>}
      </Form.Group>

      <div className="d-flex gap-2">
        <Button variant="primary" type="submit" disabled={submitting || uploading}>
          {submitting || uploading ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              {uploading ? 'Загрузка файла...' : 'Создание...'}
            </>
          ) : (
            submitLabel
          )}
        </Button>
        {onCancel && (
          <Button variant="outline-secondary" onClick={onCancel} disabled={submitting}>
            Отмена
          </Button>
        )}
      </div>
    </Form>
  );
};

export default VacationCreateForm;
